import { useState, useEffect } from 'react';

import GenericAccordian from '../../components/GenericAccordian/GenericAccordian';
import { InvertFillButton } from '../../components/InvertFillButton';
import { getFormDefaultValue, getTableHeaders } from '../../utils/forms/FormDataModel';
import { ContentItem, OnAddArguments, TabContent } from '../../utils/forms/FormInterface';
import AbstractForm from '../Entity/AbstractForm';
import AbstractLanding from '../Entity/AbstractLanding';
import AbstractTabs, { TabActiosEventType, TabActiosProps } from '../Entity/AbstractTabs';
import { HEADER_SERVICES_ENTITY } from '../../constants/EntityName';
import { headerServicesFormData, otherServicesFormData } from '../../datamodels/HeaderServices';
import callApi from '../../Api/config';

const HeaderServicesManagement = () => {
  const [activeKey, setActiveKey] = useState<string>('0');
  const [userName, setUserName] = useState<string>();
  const [showFormComponent, setShowFormComponent] = useState<boolean>(false);
  const [showOtherServicesForm, setShowOtherServicesForm] = useState<boolean>(false);
  const [refreshData, setRefreshData] = useState<boolean>(false);
  const [content, setContent] = useState<TabContent>([]);
  const [isOtherServices, setIsOtherServices] = useState<boolean>(false);
  const [existingOtherServiceId, setExistingOtherServiceId] = useState<string | null>(null);

  const tableHeaders: any = getTableHeaders(headerServicesFormData);
  const headerName = 'Header Services';

  // Check for existing Other Services on component mount and when data refreshes
  useEffect(() => {
    const checkExistingOtherServices = async () => {
      try {
        const response = await callApi(`${HEADER_SERVICES_ENTITY}?limit=100`, 'GET');

        if (response?.status && response?.data?.data?.results) {
          const existingOtherService = response.data.data.results.find(
            (service: any) => service.isOtherServices === true
          );

          if (existingOtherService) {
            setExistingOtherServiceId(existingOtherService.id);
          } else {
            setExistingOtherServiceId(null);
          }
        }
      } catch (error) {
        console.error('Error checking for existing Other Services:', error);
        setExistingOtherServiceId(null);
      }
    };

    checkExistingOtherServices();
  }, [refreshData]);

  // Transform form data before submission
  const transformFormData = (formData: any, isOtherService: boolean) => {
    // Packages are now in formData.packages array (from Form.List)
    // Filter out any empty packages
    const packages = (formData.packages || []).filter(
      (pkg: any) => pkg && pkg.packageName && pkg.packageUrl
    );

    const transformedData: any = {
      // For Other Services, auto-fill serviceName if not provided
      serviceName:
        formData.serviceName || (isOtherService ? 'Other Services' : formData.serviceName),
      packages,
      isOtherServices: isOtherService,
      // Auto-fill isActive and sortOrder for Other Services if not provided
      isActive: formData.isActive !== undefined ? formData.isActive : isOtherService ? true : true,
      sortOrder: formData.sortOrder !== undefined ? formData.sortOrder : isOtherService ? 0 : 0,
    };

    // Include icon field if present in formData (can be URL string, null, or empty string)
    // Use 'icon' in formData to check if field exists, even if value is null/empty
    if ('icon' in formData) {
      transformedData.icon = formData.icon || null;
    }

    // Include serviceUrl field if present in formData
    if ('serviceUrl' in formData) {
      transformedData.serviceUrl = formData.serviceUrl || null;
    }

    return transformedData;
  };

  // Transform API data to form format
  const transformApiDataToForm = (apiData: any) => {
    // Convert relative icon URL to full URL for preview in CMS
    let iconUrl = apiData.icon || null;
    if (iconUrl && typeof iconUrl === 'string') {
      // If it's already a full URL (http/https), use as-is
      if (iconUrl.startsWith('http://') || iconUrl.startsWith('https://')) {
        // Already a full URL, use as-is
      } else if (iconUrl.startsWith('/')) {
        // Relative path - convert to full S3 URL for preview
        const BASE_IMAGE_URL = process.env.REACT_APP_IMAGE_URL_S3 || '';
        if (
          BASE_IMAGE_URL &&
          (iconUrl.startsWith('/static-images') ||
            iconUrl.startsWith('/images') ||
            iconUrl.startsWith('/uploads') ||
            iconUrl.startsWith('/documitra-content'))
        ) {
          // Remove leading slash and construct full URL
          const cleanPath = iconUrl.replace(/^\/+/, '');
          iconUrl = `${BASE_IMAGE_URL}/${cleanPath}`;
        }
      }
    }

    const formData: any = {
      serviceName: apiData.serviceName,
      icon: iconUrl, // Include icon field from API (with full URL for preview)
      serviceUrl: apiData.serviceUrl || null, // Include serviceUrl field from API
      isActive: apiData.isActive,
      sortOrder: apiData.sortOrder || 0,
    };

    // Convert packages array to Form.List format (array of objects)
    if (apiData.packages && Array.isArray(apiData.packages)) {
      formData.packages = apiData.packages.map((pkg: any) => ({
        packageName: pkg.packageName,
        packageUrl: pkg.packageUrl,
      }));
    } else {
      formData.packages = [];
    }

    return formData;
  };

  const onSuccessful = (id?: string) => {
    if (id === 'create') {
      setShowFormComponent(false);
      setShowOtherServicesForm(false);
      // If Other Services was created, update the state
      if (isOtherServices && id) {
        setExistingOtherServiceId(id);
      }
    }
    setRefreshData(prev => !prev);
    onTabClick('0');

    setTimeout(() => {
      setRefreshData(prev => !prev);
    }, 100);
  };

  const onAddRegularService = () => {
    setIsOtherServices(false);
    setShowFormComponent(true);
    setShowOtherServicesForm(false);
    setUserName('Add Service');
    onTabClick('a');
  };

  const onAddOtherServices = async () => {
    setIsOtherServices(true);

    // Check if Other Services already exists
    try {
      const response = await callApi(`${HEADER_SERVICES_ENTITY}?limit=100`, 'GET');

      if (response?.status && response?.data?.data?.results) {
        // Find Other Services in the results
        const existingOtherService = response.data.data.results.find(
          (service: any) => service.isOtherServices === true
        );

        if (existingOtherService) {
          // Other Services exists - open in edit mode
          setExistingOtherServiceId(existingOtherService.id);
          // Make sure create form is closed
          setShowFormComponent(false);
          setShowOtherServicesForm(false);

          // Use onAdd to open in edit mode - this will add it to content tabs
          onAdd({ id: existingOtherService.id });
          return;
        }
      }
    } catch (error) {
      console.error('Error checking for existing Other Services:', error);
    }

    // Other Services doesn't exist - open create form
    setExistingOtherServiceId(null);
    setShowFormComponent(false);
    setShowOtherServicesForm(true);
    setUserName('Add Other Services');
    onTabClick('a');
  };

  const onAddNew = (argument1?: OnAddArguments) => {
    if (argument1) {
      const checkArr = content.filter((item: ContentItem) => item.key === argument1.id);
      checkArr.length ? null : onAdd(argument1);
    }
  };

  const closeTab = (key: string, shouldRefresh: boolean) => {
    setContent(content => content.filter(item => item.key !== key));
    setActiveKey('0');
    shouldRefresh && setRefreshData(prev => !prev);
    if (key === 'a') {
      setShowFormComponent(false);
    }
    // All form tabs use key 'a' (regular or other services)
    setShowOtherServicesForm(false);
  };

  const onAdd = async (argument1: OnAddArguments) => {
    // Fetch the entity data to determine if it's other services
    try {
      const entityDataResponse: any = await callApi(
        `${HEADER_SERVICES_ENTITY}/${argument1.id}`,
        'GET'
      );

      if (!entityDataResponse || !entityDataResponse.status || !entityDataResponse.data?.data) {
        console.error('Failed to fetch header service or invalid response:', entityDataResponse);
        // If fetch fails, still create the form but let AbstractForm handle the data fetching
        const newObj = {
          key: argument1.id,
          tabTitle: argument1.id,
          content: (
            <GenericAccordian key={content.length}>
              <AbstractForm
                entityId={argument1.id}
                isCreateMode={false}
                headerName={headerName}
                entityName={HEADER_SERVICES_ENTITY}
                formTemplate={headerServicesFormData}
                onSuccessfulUpdate={onSuccessful}
                formHeader=""
                showDeleteDraft={true}
                closeTab={closeTab}
                payloadProcessor={(rawPayload: any) => {
                  return transformFormData(rawPayload, false);
                }}
              />
            </GenericAccordian>
          ),
        };
        setContent((prev: any) => [...prev, newObj]);
        argument1.id && setActiveKey(String(argument1.id));
        return;
      }

      const entityData = entityDataResponse.data.data;

      // Only proceed if we have valid data
      if (!entityData || !entityData.serviceName) {
        console.error('Invalid data received:', entityData);
        // Let AbstractForm handle the fetch instead
        const newObj = {
          key: argument1.id,
          tabTitle: argument1.id,
          content: (
            <GenericAccordian key={content.length}>
              <AbstractForm
                entityId={argument1.id}
                isCreateMode={false}
                headerName={headerName}
                entityName={HEADER_SERVICES_ENTITY}
                formTemplate={headerServicesFormData}
                onSuccessfulUpdate={onSuccessful}
                formHeader=""
                showDeleteDraft={true}
                closeTab={closeTab}
                payloadProcessor={(rawPayload: any) => {
                  return transformFormData(rawPayload, false);
                }}
              />
            </GenericAccordian>
          ),
        };
        setContent((prev: any) => [...prev, newObj]);
        argument1.id && setActiveKey(String(argument1.id));
        return;
      }

      const isOther = entityData.isOtherServices || false;
      const formTemplate = isOther ? otherServicesFormData : headerServicesFormData;
      const transformedData = transformApiDataToForm(entityData);
      console.log('HeaderServicesManagement: Transformed data for form:', transformedData);
      console.log('HeaderServicesManagement: Original API data:', entityData);

      const newObj = {
        key: argument1.id,
        tabTitle: entityData.serviceName || argument1.id,
        content: (
          <GenericAccordian key={content.length}>
            <AbstractForm
              entityId={argument1.id}
              isCreateMode={false}
              headerName={headerName}
              entityName={HEADER_SERVICES_ENTITY}
              formTemplate={formTemplate}
              formDefaultPayload={transformedData}
              onSuccessfulUpdate={onSuccessful}
              formHeader=""
              showDeleteDraft={true}
              closeTab={closeTab}
              payloadProcessor={(rawPayload: any) => {
                return transformFormData(rawPayload, isOther);
              }}
            />
          </GenericAccordian>
        ),
      };

      setContent((prev: any) => [...prev, newObj]);
      argument1.id && setActiveKey(String(argument1.id));
    } catch (error) {
      console.error('Error loading service data:', error);
      // On error, still create the form but let AbstractForm handle the data fetching
      const newObj = {
        key: argument1.id,
        tabTitle: argument1.id,
        content: (
          <GenericAccordian key={content.length}>
            <AbstractForm
              entityId={argument1.id}
              isCreateMode={false}
              headerName={headerName}
              entityName={HEADER_SERVICES_ENTITY}
              formTemplate={headerServicesFormData}
              onSuccessfulUpdate={onSuccessful}
              formHeader=""
              showDeleteDraft={true}
              closeTab={closeTab}
              payloadProcessor={(rawPayload: any) => {
                return transformFormData(rawPayload, false);
              }}
            />
          </GenericAccordian>
        ),
      };
      setContent((prev: any) => [...prev, newObj]);
      argument1.id && setActiveKey(String(argument1.id));
    }
  };

  const onTabClick = (key: string) => {
    setActiveKey(key);
  };

  const updateFormVisibility = (
    action: TabActiosProps,
    targetKey?: string | TabActiosEventType
  ) => {
    if (action === 'remove') {
      if (targetKey === 'a') {
        setShowFormComponent(false);
        setShowOtherServicesForm(false);
        onTabClick('0');
      } else {
        onTabClick('0');
        setContent(content => content.filter(item => String(item.key) !== targetKey));
      }
    }
  };

  return (
    <AbstractTabs
      activeKey={activeKey}
      entityName="Header Services Management"
      updateFormVisibility={updateFormVisibility}
      onTabClick={onTabClick}
      secondTabTitle={userName}
      showFormComponent={showFormComponent || showOtherServicesForm}
      content={content}
      formComponent={
        showFormComponent ? (
          <GenericAccordian>
            <AbstractForm
              isCreateMode={true}
              headerName={headerName}
              entityName={HEADER_SERVICES_ENTITY}
              formTemplate={headerServicesFormData}
              formDefaultPayload={{
                ...getFormDefaultValue(headerServicesFormData),
                packages: [{ packageName: '', packageUrl: '' }],
              }}
              onSuccessfulUpdate={onSuccessful}
              closeTab={closeTab}
              payloadProcessor={(rawPayload: any) => {
                return transformFormData(rawPayload, false);
              }}
            />
          </GenericAccordian>
        ) : showOtherServicesForm ? (
          <GenericAccordian>
            <AbstractForm
              isCreateMode={true}
              headerName={headerName}
              entityName={HEADER_SERVICES_ENTITY}
              formTemplate={otherServicesFormData}
              formDefaultPayload={{
                ...getFormDefaultValue(otherServicesFormData),
                packages: [{ packageName: '', packageUrl: '' }],
              }}
              onSuccessfulUpdate={onSuccessful}
              closeTab={closeTab}
              payloadProcessor={(rawPayload: any) => {
                return transformFormData(rawPayload, true);
              }}
            />
          </GenericAccordian>
        ) : null
      }
    >
      <div>
        <div
          style={{
            marginBottom: '16px',
            display: 'flex',
            gap: '12px',
            position: 'absolute',
            right: 0,
          }}
        >
          <InvertFillButton
            variant="blue"
            size="lg"
            fontWeight="semibold"
            onClick={onAddRegularService}
          >
            Add Service
          </InvertFillButton>
          <InvertFillButton
            variant="white"
            size="lg"
            fontWeight="semibold"
            onClick={onAddOtherServices}
          >
            {existingOtherServiceId ? 'Edit Other Services' : 'Add Other Services'}
          </InvertFillButton>
        </div>
        <AbstractLanding
          headerName={headerName}
          addName=""
          entityName={HEADER_SERVICES_ENTITY}
          tableHeaders={tableHeaders}
          onAdd={onAddNew}
          refreshData={refreshData}
          disableAdd={true}
        />
      </div>
    </AbstractTabs>
  );
};

export default HeaderServicesManagement;
